import { Button, ListItemIcon, MenuItem } from "@mui/material";
import { CancelScheduleSend, ContentCopy, DeleteForever, Edit, ExitToApp, HelpOutlined, IosShare, OpenInNew, Redo, RestoreFromTrash, Visibility } from "@mui/icons-material";
import React, { useState } from "react";
import { useNavigate } from "react-router";
import { APIError } from "system/APIRequest";
import i18n from "translation";
import { ResourceActionDialog } from "./ResourceActionDialog";

export interface ResourceActionIconProps {
  action: string;
}

export function ResourceActionIcon(props: ResourceActionIconProps | any) {
  const { action, ...rest } = props;

  if (props.action === "view") return <Visibility {...rest} />
  if (props.action === "edit") return <Edit {...rest} />
  if (props.action === "share") return <IosShare {...rest} />
  if (props.action === "duplicate") return <ContentCopy {...rest} />
  if (props.action === "create-from") return <OpenInNew {...rest} />
  if (props.action === "reassign") return <Redo {...rest} />
  if (props.action === "unshare") return <CancelScheduleSend {...rest} />
  if (props.action === "remove") return <DeleteForever {...rest} />
  if (props.action === "restore") return <RestoreFromTrash {...rest} />
  if (props.action === "logout-sessions") return <ExitToApp {...rest} />

  return <HelpOutlined {...rest} />
}

export const ActionColors: { [key: string]: "primary" | "secondary" | "warning" | "error" | "success" | "inherit" } = {
  "share": "primary",
  "duplicate": "primary",
  "create-from": "primary",
  "reassign": "warning",
  "unshare": "warning",
  "remove": "error",
  "restore": "success",
  "logout-sessions": "warning",
  "edit": "primary",
  "view": "inherit"
}

export interface ResourceActionButtonGroupProps {
  actions: ("share" | "unshare" | "duplicate" | "create-from" | "remove" | "restore" | "reassign" | "logout-sessions" | "edit" | "view")[],
  label?: string,
  resourceIds: string[] | string,
  endpoint: string, // The resource name, sent to the API at /obj/${endpoint}
  handleActionComplete?: (wasSuccessful: boolean, resourceIds: string[], action: string, error?: APIError) => void,
  onDialogOpen?: (resourceIds: string[], action: string) => void,
  onDialogClose?: (resourceIds: string[], action: string) => void,
  asAdmin?: boolean,
  showSharedWarning?: boolean,
  reassignAccount?: boolean,
  asMenuItems?: boolean,
  defaultAccountId?: string,
  resourcePath: string, // The UI path to the resource, used for links in the app
  reassignable?: boolean, // Set to false if the resource cannot be reassigned - use to hide the target user on copy if not reassignable

  // Account-level access setting, used for Share settings
  accountAccess?: string,

  // These can be used to relabel remove and restore as something else - will be used as a key in translations
  removeAs?: string,
  restoreAs?: string
}

export function ResourceActionButtonGroup(props: ResourceActionButtonGroupProps) {
  let navigate = useNavigate();

  const [dialogAction, setDialogAction] = useState<"share" | "unshare" | "duplicate" | "create-from" | "remove" | "restore" | "reassign" | "logout-sessions">("share");
  const [dialogOpen, setDialogOpen] = useState(false);

  // Ensure resourceIds is always an array
  let resourceIds = props.resourceIds;
  if (typeof resourceIds === "string") {
    resourceIds = [resourceIds];
  }

  if (!resourceIds || resourceIds.length === 0)
    return <React.Fragment />;

  const actionLabel = (action: string): string => {
    if (action === "remove")
      return props.removeAs || "remove";
    if (action === "restore")
      return props.restoreAs || "restore";
    return action;
  }

  let buttons: JSX.Element[] = [];
  if (props.asMenuItems) {
    buttons = props.actions.map((action) => <MenuItem key={"menuitem-" + action}
      onClick={(e: React.MouseEvent<HTMLLIElement>) => {
        if (action === "edit" || action === "view") {
          navigate(props.resourcePath + "/" + resourceIds[0]);
        } else if (action === "create-from") {
          navigate(props.resourcePath + "/:create?from=" + resourceIds[0]);
        } else {
          setDialogAction(action);
          setDialogOpen(true);
          if (props.onDialogOpen) {
            props.onDialogOpen(resourceIds as string[], action);
          }
        }
      }}>
      <ListItemIcon>
        <ResourceActionIcon action={action} fontSize="small" color={ActionColors[action]} />
      </ListItemIcon>
      {i18n.t(actionLabel(action as string))}
    </MenuItem>)
  } else {
    buttons = props.actions.map((action) => <Button size="small" key={"button-" + action}
      style={{ minWidth: 120 }}
      variant="contained"
      color={ActionColors[action]}
      startIcon={<ResourceActionIcon action={action} />}
      onClick={(e: any) => {
        if (action === "edit" || action === "view") {
          navigate(props.resourcePath + "/" + resourceIds[0]);
        } else if (action === "create-from") {
          navigate(props.resourcePath + "/:create?from=" + resourceIds[0]);
        } else {
          setDialogAction(action);
          setDialogOpen(true);
          if (props.onDialogOpen) {
            props.onDialogOpen(resourceIds as string[], action);
          }
        }
      }}>
      {i18n.t(actionLabel(action as string))}
    </Button>)
  }

  if (buttons.length === 0)
    return <></>

  return (
    <>
      {props.label && <span style={{ alignSelf: "center" }}>{props.label}</span>}

      {buttons}

      {dialogOpen &&
        <ResourceActionDialog
          asAdmin={props.asAdmin}
          open={dialogOpen}
          action={dialogAction}
          resourceIds={resourceIds}
          endpoint={props.endpoint}
          defaultAccountId={props.defaultAccountId}
          onClose={(e: {}, reason: "escapeKeyDown" | "backdropClick") => {
            setDialogOpen(false);
            if (props.onDialogClose) {
              props.onDialogClose(resourceIds as string[], dialogAction);
            }
          }}
          showSharedWarning={props.showSharedWarning}
          reassignAccount={props.reassignAccount || (dialogAction === "reassign" && props.endpoint === "user")}
          accountAccess={props.accountAccess}
          reassignable={props.reassignable || props.asAdmin ? true : false}
          handleComplete={(wasSuccessful: boolean, resourceIds: string[], action: string, error?: APIError | undefined) => {
            if (props.handleActionComplete !== undefined) {
              props.handleActionComplete(wasSuccessful, resourceIds, action, error);
            }
            if (wasSuccessful) {
              setDialogOpen(false);
            }
          }} />
      }
    </>
  )
}